import { format } from "date-fns";
import { AggregateEsopReport } from "../../../api/esopStats";
import { SecondaryCTAButton } from "../../../components/CTAButtonComponents";

type ActiveAggregateEsopReportsProps = {
  reports: AggregateEsopReport[];
  onClose: () => void;
  onPreview: (report: AggregateEsopReport) => void;
};

export default function ActiveAggregateEsopReports({
  reports,
  onClose,
  onPreview,
}: ActiveAggregateEsopReportsProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[75vh] w-full max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-w-4xl">
        <div className="relative shrink-0 border-b border-gray-200 px-6 py-4">
          <button
            aria-label="Close available ESOP reports"
            className="absolute right-4 top-4 rounded p-1 text-xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
          <h2 className="pr-8 text-lg font-semibold">Available ESOP reports</h2>
          <p className="mt-1 pr-8 text-sm text-gray-500">
            Reports remain available for seven days after generation.
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {reports.length === 0 ? (
            <p className="text-sm text-gray-500">No active reports found.</p>
          ) : (
            <div className="divide-y divide-gray-200 rounded border border-gray-200">
              {reports.map((report) => (
                <div
                  className="flex items-center justify-between gap-4 p-4"
                  key={report.externalRequestId}
                >
                  <div className="min-w-0 text-sm text-gray-600">
                    <p className="font-medium text-gray-800">
                      {report.fromDate} to {report.toDate}
                    </p>
                    <p className="mt-1">
                      Generated:&nbsp;
                      {report.generatedAt
                        ? format(
                            new Date(report.generatedAt),
                            "dd-MMM-yyyy HH:mm",
                          )
                        : "—"}
                      {" · "}
                      {report.companyCount ?? 0} companies
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Available until:&nbsp;
                      {report.expiresAt
                        ? format(
                            new Date(report.expiresAt),
                            "dd-MMM-yyyy HH:mm",
                          )
                        : "—"}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <SecondaryCTAButton onClick={() => onPreview(report)}>
                      Preview
                    </SecondaryCTAButton>
                    <SecondaryCTAButton
                      disabled={!report.downloadUrl}
                      onClick={() =>
                        window.open(
                          report.downloadUrl || undefined,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      Download
                    </SecondaryCTAButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
